import React, { useState, useEffect } from 'react';
import { DollarSign, Save, Trash2, UserPlus } from 'lucide-react';

export type PayType = 'hourly' | 'salary';

export interface StaffPayRate {
  staffName: string;
  payType: PayType;
  hourlyRate: number; // Used when payType is 'hourly'
  salary: number; // Used when payType is 'salary' (bi-weekly amount)
}

const STORAGE_KEY = 'ltss_staff_pay_rates';

export function getStaffPayRates(): StaffPayRate[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveStaffPayRates(rates: StaffPayRate[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rates));
}

interface StaffPayRatesProps {
  knownStaffNames?: string[];
}

export function StaffPayRates({ knownStaffNames = [] }: StaffPayRatesProps) {
  const [rates, setRates] = useState<StaffPayRate[]>([]);
  const [newStaffName, setNewStaffName] = useState('');
  const [newPayType, setNewPayType] = useState<PayType>('hourly');
  const [newRate, setNewRate] = useState('');
  const [newSalary, setNewSalary] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setRates(getStaffPayRates());
  }, []);

  // Filter suggestions based on input
  const suggestions = newStaffName.trim().length > 0
    ? knownStaffNames.filter(name => 
        name.toLowerCase().includes(newStaffName.toLowerCase()) &&
        !rates.some(r => r.staffName.toLowerCase() === name.toLowerCase())
      ).slice(0, 8)
    : [];

  const handleRateChange = (staffName: string, field: 'hourlyRate' | 'salary', value: string) => {
    const numValue = parseFloat(value) || 0;
    setRates(prev => 
      prev.map(r => r.staffName === staffName ? { ...r, [field]: numValue } : r)
    );
    setHasChanges(true);
  };

  const handlePayTypeChange = (staffName: string, payType: PayType) => {
    setRates(prev => 
      prev.map(r => r.staffName === staffName ? { ...r, payType } : r)
    );
    setHasChanges(true);
  };

  const handleAddStaff = () => {
    if (!newStaffName.trim()) return;
    const rate = parseFloat(newRate) || 0;
    
    // Check if already exists
    if (rates.some(r => r.staffName.toLowerCase() === newStaffName.toLowerCase())) {
      alert('Staff member already exists');
      return;
    }

    const salary = parseFloat(newSalary) || 0;
    setRates(prev => [...prev, { 
      staffName: newStaffName.trim(), 
      payType: newPayType,
      hourlyRate: rate,
      salary: salary
    }]);
    setNewStaffName('');
    setNewPayType('hourly');
    setNewRate('');
    setNewSalary('');
    setHasChanges(true);
    setShowSuggestions(false);
  };

  const handleSelectSuggestion = (name: string) => {
    setNewStaffName(name);
    setShowSuggestions(false);
  };

  const handleRemoveStaff = (staffName: string) => {
    setRates(prev => prev.filter(r => r.staffName !== staffName));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveStaffPayRates(rates);
    setHasChanges(false);
  };

  // Get staff from reports that don't have rates set
  const missingStaff = knownStaffNames.filter(
    name => !rates.some(r => r.staffName.toLowerCase() === name.toLowerCase())
  );

  const handleAddFromReport = (staffName: string) => {
    setRates(prev => [...prev, { staffName, payType: 'hourly', hourlyRate: 0, salary: 0 }]);
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-200">Staff Pay Rates</h3>
          <p className="text-sm text-slate-400 mt-1">
            Set hourly pay rates for payroll estimation
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        )}
      </div>

      {/* Current Staff Rates */}
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                Staff Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                Pay Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">
                Rate / Salary
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {rates.map((rate) => (
              <tr key={rate.staffName} className="hover:bg-slate-700/30">
                <td className="px-4 py-3 text-sm text-slate-200">
                  {rate.staffName}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={rate.payType || 'hourly'}
                    onChange={(e) => handlePayTypeChange(rate.staffName, e.target.value as PayType)}
                    className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  {(rate.payType || 'hourly') === 'hourly' ? (
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rate.hourlyRate || ''}
                        onChange={(e) => handleRateChange(rate.staffName, 'hourlyRate', e.target.value)}
                        className="w-24 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                        placeholder="0.00"
                      />
                      <span className="text-sm text-slate-400">/hr</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-blue-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={rate.salary || ''}
                        onChange={(e) => handleRateChange(rate.staffName, 'salary', e.target.value)}
                        className="w-28 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                        placeholder="0.00"
                      />
                      <span className="text-sm text-slate-400">/bi-weekly</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleRemoveStaff(rate.staffName)}
                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {rates.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No staff pay rates configured. Add staff below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add New Staff */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-3">Add Staff Member</h4>
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <input
              type="text"
              value={newStaffName}
              onChange={(e) => {
                setNewStaffName(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Start typing staff name..."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
            />
            {/* Autocomplete suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {suggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectSuggestion(name)}
                    className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-600 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <select
            value={newPayType}
            onChange={(e) => setNewPayType(e.target.value as PayType)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
          >
            <option value="hourly">Hourly</option>
            <option value="salary">Salary</option>
          </select>
          {newPayType === 'hourly' ? (
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4 text-green-400" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="17.00"
                className="w-24 px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
              />
              <span className="text-xs text-slate-400">/hr</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4 text-blue-400" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={newSalary}
                onChange={(e) => setNewSalary(e.target.value)}
                placeholder="1500.00"
                className="w-28 px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
              />
              <span className="text-xs text-slate-400">/bi-wk</span>
            </div>
          )}
          <button
            onClick={handleAddStaff}
            disabled={!newStaffName.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {/* Staff from Reports without Rates */}
      {missingStaff.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-400 mb-2">
            Staff from Reports Missing Pay Rates ({missingStaff.length})
          </h4>
          <p className="text-xs text-slate-400 mb-3">
            These staff appear in your uploaded reports but don't have pay rates set.
          </p>
          <div className="flex flex-wrap gap-2">
            {missingStaff.slice(0, 10).map((name) => (
              <button
                key={name}
                onClick={() => handleAddFromReport(name)}
                className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
              >
                + {name}
              </button>
            ))}
            {missingStaff.length > 10 && (
              <span className="px-3 py-1.5 text-xs text-slate-500">
                +{missingStaff.length - 10} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
